import { useState } from "react";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type HeroProps = {
  onEmailContinue?: (email: string) => void;
};

export default function Hero({ onEmailContinue }: HeroProps) {
  const [heroEmail, setHeroEmail] = useState("");
  const [heroEmailError, setHeroEmailError] = useState("");

  function handleHeroJoin() {
    const v = heroEmail.trim();
    if (!EMAIL_RE.test(v)) {
      setHeroEmailError("Enter a valid email address.");
      return;
    }
    setHeroEmailError("");
    onEmailContinue?.(v);
  }

  return (
    <section
      id="homepage-hero"
      className="w-full bg-black font-fira text-white"
    >
      <div className="mx-auto w-[90%] mt-4 md:w-[98%]">
        <div className="rounded-b-2xl rounded-t-none bg-gradient-to-b from-white/0 to-white/10 p-px md:rounded-b-3xl">
          <div className="relative min-h-[min(85dvh,50rem)] overflow-hidden rounded-2xl  md:min-h-[min(90dvh,48rem)] md:rounded-b-3xl">
            <picture className="pointer-events-none absolute inset-0">
              <source
                media="(min-width: 768px)"
                srcSet="/homepage/saherod.webp"
              />
              <img
                src="/homepage/saherom.webp"
                alt=""
                className="h-full w-full object-cover object-bottom"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
            </picture>

            <div className="relative z-10 flex min-h-[min(85dvh,50rem)] flex-col justify-end py-10 md:min-h-[min(90dvh,48rem)] md:justify-center md:py-14">
              <div className="mx-auto flex w-[90%] flex-col items-start text-left md:w-[80%]">
                <div className="mb-4 md:hidden">
                  <span className="inline-flex items-center gap-2 rounded-md border border-white/25 bg-black/40 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white backdrop-blur-sm">
                    Free <span className="text-white/50">•</span> Whatsapp Community
                  </span>
                </div>

                <h1 className="max-w-2xl font-fira-condensed text-[32px] font-medium leading-tight tracking-tight text-white md:text-[46px]">
                  <span className="block md:inline">Stay ahead of AI.</span>{" "}
                  <span className="block md:inline">Together</span>
                </h1>

                <p className="mt-4 w-full max-w-xl text-[13px] font-normal leading-relaxed text-[#FFFFFF85] sm:mt-5 md:mt-6 md:text-[15px]">
                  Skip the noise. Get the signal. AI updates, tools, and live
                  sessions straight to Whatsapp
                </p>

                <p className="mt-4 w-full text-sm text-white/55 sm:mt-5 md:mt-6">
                  Curated by{" "}
                  <span className="font-semibold text-white">
                    Vaibhav Sisinty
                  </span>
                  <span className="text-white/40"> · </span>
                  <span className="text-white/55">Founder, GrowthSchool</span>
                </p>

                <div
                  id="join"
                  className="mt-6 w-full max-w-lg scroll-mt-28 sm:mt-8 sm:scroll-mt-32 md:mt-10"
                >
                  <div
                    className="flex flex-col gap-2 sm:gap-3 md:flex-row md:items-stretch"
                    role="group"
                    aria-label="Email signup"
                  >
                    <label className="sr-only" htmlFor="hero-email">
                      Email address
                    </label>

                    {/* Wrapper: p-px + bg creates the 1px border on mobile; glow orb travels along it */}
                    <div className="relative min-w-0 flex-1 overflow-hidden rounded-lg p-px bg-white/15 md:p-0 md:bg-transparent">
                      <div
                        className="pointer-events-none absolute h-[110px] w-[110px] rotate-45 md:hidden"
                        style={{
                          offsetPath: "rect(0% auto 100% auto)",
                          animation: "border-glow-move 4.5s linear infinite",
                          background: "radial-gradient(circle, white 0%, rgba(255,255,255,0.6) 25%, rgba(255,255,255,0.15) 55%, transparent 70%)",
                        }}
                        aria-hidden
                      />
                      <input
                        id="hero-email"
                        name="email"
                        type="email"
                        autoComplete="email"
                        placeholder="Enter your email"
                        value={heroEmail}
                        onChange={(e) => {
                          setHeroEmail(e.target.value);
                          if (heroEmailError) setHeroEmailError("");
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleHeroJoin();
                          }
                        }}
                        aria-invalid={!!heroEmailError}
                        aria-describedby={
                          heroEmailError ? "hero-email-error" : undefined
                        }
                        className={`relative z-10 min-h-12 w-full rounded-[6px] bg-[#0a0a0a] px-3 py-3 text-center font-fira text-sm text-white placeholder:text-white/[0.72] focus:outline-none sm:px-4 sm:text-[0.9375rem] md:rounded-lg md:border md:bg-black/40 md:backdrop-blur-sm md:text-left md:focus:ring-1 md:focus:ring-white/30 ${
                          heroEmailError
                            ? "md:border-red-400/50 md:focus:border-red-400/50"
                            : "md:border-white/20 md:focus:border-white/40"
                        }`}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={handleHeroJoin}
                      className="min-h-12 shrink-0 rounded-lg border border-[#FFFFFF4D] bg-white px-4 py-3 font-fira text-sm font-semibold text-black transition-opacity hover:opacity-90 sm:px-6 sm:text-[0.9375rem]"
                    >
                      <span className="md:hidden">Join Community</span>
                      <span className="hidden md:inline">Join Now</span>
                    </button>
                  </div>
                  {heroEmailError ? (
                    <p
                      id="hero-email-error"
                      className="mt-2 text-xs text-red-400"
                      role="alert"
                    >
                      {heroEmailError}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}